import React, { FC } from 'react';

import styled from 'styled-components';
import i18n, { translations } from 'i18n';

import { BannerCarousel } from '../BannerCarousel/BannerCarousel';
import PremiumCollectionHeader from '../Premium/PremiumCollectionHeader';
import { PageLayout } from '../../../types/page-layout';
import { Banner, ItemLink, RichBanner } from '../../../types/banner';
import { AppType, PageLayoutItemType } from '../../../constants';
import { ItemsPreview } from '../ItemsPreview';
import App from '../../../models/app';
import { getResourceType, getType } from '../../../utils.v2';
import { AppList } from '../AppList/AppList';
import { ClockList } from '../ClockList/ClockList';
import { Link } from '../Common';

import Icon from '../Icon';
import EditIconWhite from '../../../icons/ic_edit_white.png';
import { WarningBanner } from '../WarningBanner/WarningBanner';

const LinkContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 4px 16px;
  gap: 10px;

  width: 191px;
  height: 48px;

  color: #FFFFFF;

  background: ${({ theme }) => theme.fitbitGreenV2};
  border-radius: 16px;

  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 24px;

  letter-spacing: -0.28px;
  text-transform: uppercase;

  color: #FFFFFF;


  flex: none;
  order: 1;
  align-self: center;
  flex-grow: 0;

  margin: auto;
  margin-top: 36px;
  margin-bottom: 30px;
`;

interface DiscoveryAppListProps {
  isClock?: boolean;
  appType?: AppType;
  apps: App[];
}

const DiscoveryAppList: FC<DiscoveryAppListProps> = ({ isClock, apps, appType }) =>
  isClock ? (
    <ClockList apps={apps} />
  ) : (
    <React.Fragment>
      <AppList
        apps={apps}
      />
      {
        appType && appType === AppType.TILE && (
          <Link
            to={'/v2/my_tiles'}
            pageDirection='FORWARD'
            id={'tiles-gallery'}
            aria-label={'tiles-gallery'}
            key={`manage-my-tiles`}
          >
            <LinkContainer>
              <Icon
                id={`manage-tiles-button`}
                aria-label={`manage-tiles-button`}
                url={EditIconWhite}
                width={19}
                height={19}
              />
              {i18n('page.ManageTiles.titleNoNumber')()}
            </LinkContainer>
          </Link>
        )
      }
    </React.Fragment>
  );

const euKeyword = 'EUROPE';

interface DiscoveryEntryProps {
  fscViewName: string;
  pageLayoutItems: PageLayout[];
  userHasGalleryGrant: boolean;
  isClock?: boolean;
  appType: AppType;
}

export const DiscoveryEntry: FC<DiscoveryEntryProps> = ({
  fscViewName,
  pageLayoutItems = [],
  userHasGalleryGrant,
  isClock,
  appType
}) => {
  const shouldDisplayEUBanner = (banners: Banner[]): boolean => {
    if (banners.length > 1) {
      return false;
    }

    if (banners[0].button.text !== euKeyword) {
      return false;
    }

    return true;
  };

  const composeBannerContent = (banner: Banner): RichBanner => {
    const title = `<b>${translations.euAnnouncement.title()}</b>`;
    const linkElement = `<a href="${banner.text}">${translations.euAnnouncement.link()}</a>`;
    const text = translations.euAnnouncement.text({link: linkElement});

    const content = `${title} ${text}`;
    return {
      content
    };
  };

  return (
    <div>
      {pageLayoutItems.map((layoutItem, index) => {
        switch (layoutItem.type) {
          case PageLayoutItemType.BANNER_LIST:
            return (shouldDisplayEUBanner(layoutItem.banners as Banner[])
              ? <WarningBanner 
                banner={composeBannerContent(layoutItem.banners![0] as Banner)} 
                key={`layout-item-${index}`} />
              : <BannerCarousel
                banners={layoutItem.banners as Banner[]}
                fscViewName={fscViewName}
                key={`layout-item-${index}`} />
            );
          case PageLayoutItemType.APP_LIST:
            if (!layoutItem.apps || !layoutItem.apps.length) {
              return;
            }

            const { type, resourceId } = layoutItem.link as ItemLink;
            const resourceType = getResourceType(getType())

            return (
              <ItemsPreview
                id={resourceId}
                collectionType={type}
                isClock={isClock}
                appType={appType}
                name={layoutItem.title as string}
                apps={layoutItem.apps as App[]}
                fscViewName={fscViewName}
                path={`/v2/${resourceType}/${type.toLowerCase()}/${resourceId}`}
                key={`layout-item-${index}`}
                shouldDisplayViewAll={true}
                footer={isClock && layoutItem.isPremium
                  ? <PremiumCollectionHeader
                    userHasGalleryGrant={userHasGalleryGrant}
                    subscribeButtonTrigger={`collection-${resourceId}`}
                  />
                  : undefined}
              />
            );
          // Full array of apps displayed as one list for tracker gallery and CU1
          case 'FLAT_APP_LIST':
            return (
              <DiscoveryAppList
                apps={layoutItem.apps as App[]}
                key={`layout-item-${index}`}
                isClock={isClock}
                appType={appType}
              />
            );
          default:
          // do nothing
        }
        return undefined;
      })}
    </div>
  )
};
